import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateId } from '../../utils/formatting.js';
import WizardShell from '../shared/WizardShell.jsx';
import WelcomeStep from './steps/WelcomeStep.jsx';
import RiskIntroStep from './steps/RiskIntroStep.jsx';
import RiskCreateStep, { DEFAULT_RISK_DRAFT } from './steps/RiskCreateStep.jsx';
import TaskCreateStep, { defaultTaskDraft } from './steps/TaskCreateStep.jsx';
import CompleteStep from './steps/CompleteStep.jsx';

export const ONBOARDING_TITLE = 'Get started with Tryggr';
export const SKIP_MESSAGE = "Skip onboarding? You won't see this guide again.";
export const SKIP_WITHOUT_ACTION_MESSAGE =
  "Your risk has no action yet. Skip onboarding anyway? You won't see this guide again.";

/**
 * First-login guide. Closing it marks onboarding complete for good, so the
 * only way out is the header ✕, always behind a confirm that says so — a
 * backdrop click does nothing. The risk and the task are each written once,
 * in their step's validate(); Back shows them read-only and Next never
 * creates a second one.
 */
export default function OnboardingWizard({
  session,
  state,
  users,
  tenantId: _tenantId,
  saveRisk,
  saveTask,
  onComplete,
  onStartPart2,
}) {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [riskDraft, setRiskDraft] = useState(DEFAULT_RISK_DRAFT);
  const [taskDraft, setTaskDraft] = useState(() => defaultTaskDraft(session));
  const [createdRisk, setCreatedRisk] = useState(
    /** @type {{id: string, name: string} | null} */ (null)
  );
  const [taskCreated, setTaskCreated] = useState(false);

  const createRisk = async () => {
    if (createdRisk) return null;
    if (!riskDraft.name.trim()) return 'Give the risk a name.';
    // ID-GEN-001: `id` here is a throwaway local key for saveRisk's
    // optimistic insert — the server always mints the real RISK-NNNN id
    // (handlers/create.js ignores this value entirely), so downstream
    // state (and the task created in the next step, linked by riskId) must
    // use the server-confirmed id from the resolved write, not this guess.
    try {
      const created = await saveRisk({
        id: generateId(),
        name: riskDraft.name,
        scenario: riskDraft.description,
        type: 'risk',
        status: 'open',
        likelihood: riskDraft.likelihood,
        impact: riskDraft.impact,
        controls: [],
        tasks: [],
      });
      setCreatedRisk({ id: created.id, name: riskDraft.name });
      return null;
    } catch {
      return 'The risk could not be saved. Try again.';
    }
  };

  const createTask = async () => {
    if (taskCreated) return null;
    if (!taskDraft.title.trim()) return 'Give the task a title.';
    if (!createdRisk) return 'Go back and add the risk first.';
    try {
      await saveTask({
        id: generateId(),
        title: taskDraft.title,
        description: taskDraft.description,
        type: 'manual',
        taskType: 'evidence-collection',
        status: 'open',
        createdAt: new Date().toISOString(),
        dueDate: taskDraft.dueDate,
        assigneeId: taskDraft.assigneeId,
        sourceType: 'risk',
        evidence: [],
        controls: [],
        riskId: createdRisk.id,
        riskName: createdRisk.name,
      });
      setTaskCreated(true);
      return null;
    } catch {
      return 'The task could not be saved. Try again.';
    }
  };

  const handleFinish = () => {
    onComplete();
    navigate('/dashboard');
  };

  const steps = [
    { id: 'welcome', label: 'Welcome', body: <WelcomeStep /> },
    { id: 'risk-intro', label: 'What could go wrong', body: <RiskIntroStep /> },
    {
      id: 'risk',
      label: 'Your first risk',
      body: (
        <RiskCreateStep
          value={riskDraft}
          onChange={setRiskDraft}
          state={state}
          createdId={createdRisk?.id}
        />
      ),
      validate: createRisk,
    },
    {
      id: 'task',
      label: 'Your first action',
      body: (
        <TaskCreateStep
          value={taskDraft}
          onChange={setTaskDraft}
          users={users}
          created={taskCreated}
        />
      ),
      validate: createTask,
    },
    {
      id: 'complete',
      label: 'Done',
      body: (
        <CompleteStep
          onStartPart2={() => {
            handleFinish();
            if (onStartPart2) onStartPart2();
          }}
        />
      ),
    },
  ];

  const onLastStep = stepIndex === steps.length - 1;

  return (
    <WizardShell
      title={ONBOARDING_TITLE}
      steps={steps}
      onClose={onComplete}
      onFinish={handleFinish}
      onStepChange={setStepIndex}
      closeOnBackdrop={false}
      dirty={!onLastStep}
      dirtyMessage={createdRisk && !taskCreated ? SKIP_WITHOUT_ACTION_MESSAGE : SKIP_MESSAGE}
    />
  );
}
